import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    question: "What is a custom nightguard for sleep apnea and how does it work?",
    answer: "A custom nightguard for sleep apnea is a specially designed oral appliance that repositions your jaw and tongue to keep your airway open while you sleep. At Sunshine Dental Care in Roseville, Dr. Maichou Yang creates custom-fitted nightguards that are more comfortable and effective than over-the-counter options. These devices are an excellent alternative to CPAP machines for mild to moderate sleep apnea."
  },
  {
    question: "What is an airway dentist and what do they do?",
    answer: "An airway dentist is a dental professional trained to evaluate, diagnose, and treat airway-related sleep disorders including sleep apnea and snoring. Dr. Maichou Yang is a qualified airway dentist in Roseville, CA who assesses how your oral structures affect your breathing during sleep. She provides treatments like custom oral appliances, nightguards, and collaborates with sleep physicians to ensure comprehensive care."
  },
  {
    question: "How can a sleep apnea dentist in Roseville help me?",
    answer: "A sleep apnea dentist in Roseville, CA like Dr. Maichou Yang at Sunshine Dental Care can provide oral appliance therapy as an alternative to CPAP. We offer custom nightguards for sleep apnea that are comfortable, portable, and effective. Our sleep apnea treatment includes comprehensive evaluation, custom device fitting, and ongoing care to ensure optimal results for better sleep and improved health."
  },
  {
    question: "What are the symptoms of sleep apnea?",
    answer: "Common symptoms of sleep apnea include loud snoring, gasping or choking during sleep, waking up with a dry mouth or headache, excessive daytime sleepiness, difficulty concentrating, and irritability. If you experience these symptoms, contact our sleep apnea dentist in Roseville at (916) 773-1122 for an evaluation. Early treatment can prevent serious health complications."
  },
  {
    question: "Is a dental nightguard better than CPAP for sleep apnea?",
    answer: "For mild to moderate sleep apnea, a custom dental nightguard (oral appliance) can be as effective as CPAP and is often preferred by patients because it's more comfortable, portable, quiet, and easy to use. Dr. Yang, our airway dentist in Roseville, CA, will evaluate your condition and recommend the best treatment option. Many patients find oral appliances easier to comply with long-term."
  },
  {
    question: "What services does Sunshine Dental Care offer?",
    answer: "Sunshine Dental Care offers comprehensive dental services including custom nightguards for sleep apnea, airway dentistry, preventative care, cosmetic dentistry, same-day CEREC crowns, dental implants, teeth whitening, porcelain veneers, orthodontics (Invisalign and Clear Correct), emergency dental care, restorative dentistry, and oral surgery. We serve patients of all ages in Roseville, CA and surrounding areas."
  },
  {
    question: "Does Sunshine Dental Care accept emergency appointments?",
    answer: "Yes, we offer same-day emergency dental appointments. If you're experiencing a dental emergency such as severe tooth pain, a broken tooth, or dental trauma, call us immediately at (916) 773-1122. Dr. Maichou Yang and our team prioritize emergency cases to provide prompt relief and treatment."
  },
  {
    question: "What are same-day crowns and how do they work?",
    answer: "Same-day crowns use CEREC (Chairside Economical Restoration of Esthetic Ceramics) technology to create and place dental crowns in a single visit. Using 3D imaging and CAD/CAM technology, we design and mill your custom crown right in our office, eliminating the need for temporary crowns and multiple appointments. The entire process typically takes about 2 hours."
  },
  {
    question: "Who is Dr. Maichou Yang?",
    answer: "Dr. Maichou Yang, DDS, is the lead dentist and airway dentist at Sunshine Dental Care in Roseville, CA. She specializes in family dentistry, cosmetic dentistry, and sleep apnea treatment with custom nightguards. Dr. Yang believes in building lasting relationships with patients and empowering them to take care of their dental and sleep health through education and prevention."
  },
  {
    question: "What areas does Sunshine Dental Care serve?",
    answer: "Sunshine Dental Care proudly serves patients seeking a sleep apnea dentist and airway dentist from Roseville, Rocklin, Lincoln, Citrus Heights, Sacramento, Granite Bay, Fair Oaks, Auburn, and Orangevale. Our convenient location at 700 Sunrise Ave, Ste E, Roseville, CA 95661 is easily accessible from throughout the Greater Sacramento area."
  },
  {
    question: "What are the office hours at Sunshine Dental Care?",
    answer: "Sunshine Dental Care is open Monday through Wednesday from 7:00 AM to 3:30 PM. We recommend scheduling appointments in advance, though same-day emergency appointments are available. Call us at (916) 773-1122 to schedule your visit for sleep apnea treatment, custom nightguards, or any dental service."
  },
  {
    question: "How can I schedule an appointment at Sunshine Dental Care?",
    answer: "You can schedule an appointment by calling us at (916) 773-1122 or by filling out the contact form on our website. We welcome new patients seeking a sleep apnea dentist, airway dentist, or general dental care in Roseville, CA. Our friendly staff will help you find a convenient appointment time."
  },
  {
    question: "Does Sunshine Dental Care treat patients with dental anxiety?",
    answer: "Yes, we specialize in treating patients with dental anxiety. Dr. Yang and our team create a comfortable, welcoming environment and take extra care with anxious patients. We explain procedures thoroughly, work at your pace, and offer various comfort options to ensure a positive dental experience."
  }
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-cyan-600 font-semibold tracking-wider uppercase text-sm">
            Common Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find answers to common questions about our dental services, procedures, and office policies.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                aria-expanded={openIndex === index}
                data-testid={`faq-question-${index}`}
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="text-cyan-600 flex-shrink-0" size={20} />
                ) : (
                  <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Don't see your question? We're here to help!
          </p>
          <a
            href="tel:9167731122"
            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Call Us: (916) 773-1122
          </a>
        </div>
      </div>
    </section>
  );
};

// Export FAQs for schema generation
export { faqs };
export default FAQSection;
